import { useReducer, useEffect, useMemo, useRef, useCallback } from "react";
import { useLazyGetDatasetQuestionsQuery } from "@store/api/dataset-api";
import { useDebounce } from "@/hooks/use-debounce";
import type { DatasetQuestionsSearchResponse } from "@models/Dataset";
import type { QuestionSelection } from "@store/slices/crosstab-config-slice";
import { questionSessionMemory } from "@/utils/question-session-memory";

interface UseCrosstabQuestionSelectionProps {
  datasetId: string | null;
  questionType: "row" | "column";
  dashboardId?: string;
  /** Defaults to true (Crosstab's row/column pickers always want the list
   *  ready immediately). Line Chart's Breakout section passes false until
   *  the researcher turns Breakout on, so the candidate-question list isn't
   *  fetched for every chart that merely has Breakout available. */
  enabled?: boolean;
}

// State type
interface QuestionSelectionState {
  searchTerm: string;
  currentPage: number;
  allQuestions: DatasetQuestionsSearchResponse[];
  hasMore: boolean;
  hasError: boolean;
}

// Action types
type QuestionSelectionAction =
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "SET_CURRENT_PAGE"; payload: number }
  | { type: "SET_ALL_QUESTIONS"; payload: DatasetQuestionsSearchResponse[] }
  | { type: "APPEND_QUESTIONS"; payload: DatasetQuestionsSearchResponse[] }
  | { type: "SET_HAS_MORE"; payload: boolean }
  | { type: "SET_HAS_ERROR"; payload: boolean }
  | { type: "RESET_PAGINATION" };

// Initial state
const initialState: QuestionSelectionState = {
  searchTerm: "",
  currentPage: 1,
  allQuestions: [],
  hasMore: true,
  hasError: false,
};

// Reducer function
const questionSelectionReducer = (
  state: QuestionSelectionState,
  action: QuestionSelectionAction,
): QuestionSelectionState => {
  switch (action.type) {
    case "SET_SEARCH_TERM":
      return { ...state, searchTerm: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_ALL_QUESTIONS":
      return { ...state, allQuestions: action.payload };
    case "APPEND_QUESTIONS":
      // Filter out duplicates based on datasetElementId
      const existingIds = new Set(
        state.allQuestions.map((q) => q.datasetElementId),
      );
      const newQuestions = action.payload.filter(
        (q) => !existingIds.has(q.datasetElementId),
      );
      return {
        ...state,
        allQuestions: [...state.allQuestions, ...newQuestions],
      };
    case "SET_HAS_MORE":
      return { ...state, hasMore: action.payload };
    case "SET_HAS_ERROR":
      return { ...state, hasError: action.payload };
    case "RESET_PAGINATION":
      return {
        ...state,
        currentPage: 1,
        allQuestions: [],
        hasMore: true,
        hasError: false,
      };
    default:
      return state;
  }
};

export const useCrosstabQuestionSelection = ({
  datasetId,
  questionType,
  dashboardId = "",
  enabled = true,
}: UseCrosstabQuestionSelectionProps) => {
  const [state, dispatchState] = useReducer(
    questionSelectionReducer,
    initialState,
  );

  // Keep a stable ref to the latest state values so the callback ref observer
  // always reads fresh values without needing to re-register.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  });

  const isFetchingRef = useRef(false);
  const datasetIdRef = useRef(datasetId);
  const debouncedSearchTermRef = useRef("");
  const questionTypesRef = useRef<string[]>([]);
  const currentPageRef = useRef(1);

  const [trigger, { data: questionsData, isFetching }] =
    useLazyGetDatasetQuestionsQuery();
  const [triggerPinned, { data: pinnedQuestionsData }] =
    useLazyGetDatasetQuestionsQuery();

  // Debounce search term for API requests
  const debouncedSearchTerm = useDebounce(state.searchTerm, 500);

  // Determine question types based on selector type - memoized to prevent infinite loops
  const questionTypes = useMemo(() => {
    if (questionType === "row") {
      return ["SINGLE", "MULTI", "FLOAT", "NUMERIC"];
    } else {
      return ["SINGLE", "MULTI"];
    }
  }, [questionType]);

  // Keep refs in sync with latest values
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    datasetIdRef.current = datasetId;
  }, [datasetId]);
  useEffect(() => {
    debouncedSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);
  useEffect(() => {
    questionTypesRef.current = questionTypes;
  }, [questionTypes]);
  useEffect(() => {
    currentPageRef.current = state.currentPage;
  }, [state.currentPage]);

  const triggerNextPage = useCallback(() => {
    const s = stateRef.current;
    if (
      s.hasMore &&
      !isFetchingRef.current &&
      !s.hasError &&
      datasetIdRef.current &&
      s.allQuestions.length > 0
    ) {
      const nextPage = currentPageRef.current + 1;
      dispatchState({ type: "SET_CURRENT_PAGE", payload: nextPage });
      trigger({
        id: datasetIdRef.current,
        page: nextPage,
        page_size: 10,
        question_type: questionTypesRef.current,
        search_query: debouncedSearchTermRef.current || undefined,
      })
        .unwrap()
        .catch(() => {
          dispatchState({ type: "SET_HAS_ERROR", payload: true });
          dispatchState({ type: "SET_HAS_MORE", payload: false });
        });
    }
  }, [trigger]);

  const observerTarget = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            triggerNextPage();
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(node);
      // No cleanup needed — the node unmounts when accordion closes anyway
    },
    [triggerNextPage],
  );

  // Load first page when dataset or search term changes
  useEffect(() => {
    if (datasetId && enabled) {
      dispatchState({ type: "RESET_PAGINATION" });
      trigger({
        id: datasetId,
        page: 1,
        page_size: 10,
        question_type: questionTypes,
        search_query: debouncedSearchTerm || undefined,
      })
        .unwrap()
        .catch(() => {
          dispatchState({ type: "SET_HAS_ERROR", payload: true });
        });
    }
  }, [datasetId, debouncedSearchTerm, trigger, questionTypes, enabled]);

  // Accumulate questions as pages load
  useEffect(() => {
    if (questionsData?.questions) {
      if (state.currentPage === 1) {
        dispatchState({
          type: "SET_ALL_QUESTIONS",
          payload: questionsData.questions,
        });
      } else {
        dispatchState({
          type: "APPEND_QUESTIONS",
          payload: questionsData.questions,
        });
      }
      dispatchState({
        type: "SET_HAS_MORE",
        payload: questionsData.pagination.hasNextPage,
      });
    }
  }, [questionsData, state.currentPage]);

  // Handler for search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchState({ type: "SET_SEARCH_TERM", payload: event.target.value });
  };

  // Convert API question to QuestionSelection format
  const convertToQuestionSelection = (
    question: DatasetQuestionsSearchResponse,
  ): QuestionSelection => {
    const type = (question.type || "").toUpperCase();
    return {
      questionId: question.datasetElementId,
      questionLabel: question.label,
      questionTitle: question.title,
      questionType:
        type === "SINGLE"
          ? "Single"
          : type === "MULTI" || type === "MULTIPLE"
            ? "Multiple"
            : type === "FLOAT"
              ? "Float"
              : "Number",
      variableCount: question.variablesCount,
      responseCount: question.responses,
    };
  };

  const slot = questionType === "row" ? "row" : "column";

  const recentQuestionIds = useMemo(() => {
    if (!dashboardId || !datasetId) return [];

    // Prefer slot-specific crosstab history (row/column).
    // Fall back to general history (bar/donut/table/line) so that questions
    // used in other chart types still surface as "recently used" when no
    // crosstab-specific history exists yet for this slot.
    const slotSpecific = questionSessionMemory
      .getRecent(dashboardId, datasetId, slot)
      .map((e) => e.questionId);

    if (slotSpecific.length > 0) return slotSpecific;

    return questionSessionMemory
      .getRecent(dashboardId, datasetId, "general")
      .map((e) => e.questionId);
  }, [dashboardId, datasetId, slot]);

  // Fetch pinned questions by ID from the API when dataset is selected
  useEffect(() => {
    if (!datasetId || !dashboardId || !enabled || recentQuestionIds.length === 0) return;
    triggerPinned({
      id: datasetId,
      dataset_element_ids: recentQuestionIds,
      page_size: recentQuestionIds.length,
      question_type: questionTypes,
    });
  }, [datasetId, dashboardId, enabled, recentQuestionIds, triggerPinned, questionTypes]);

  const orderedQuestions = useMemo(() => {
    if (state.searchTerm || recentQuestionIds.length === 0) return state.allQuestions;

    // Use API-fetched pinned questions when available; fall back to scanning allQuestions
    const pinnedSource = pinnedQuestionsData?.questions ?? state.allQuestions;
    const recentSet = new Set(recentQuestionIds);

    const pinned = recentQuestionIds
      .map((id) => pinnedSource.find((q) => q.datasetElementId === id))
      .filter(Boolean) as DatasetQuestionsSearchResponse[];

    const rest = state.allQuestions.filter((q) => !recentSet.has(q.datasetElementId));
    return [...pinned, ...rest];
  }, [state.allQuestions, state.searchTerm, recentQuestionIds, pinnedQuestionsData]);

  return {
    questions: orderedQuestions,
    searchTerm: state.searchTerm,
    isFetching,
    hasMore: state.hasMore,
    observerTarget,
    handleSearchChange,
    convertToQuestionSelection,
    recentQuestionIds,
  };
};
